import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Search, UserX, ArrowRight, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

type ReplaceMemberDialogProps = {
  type: 'team' | 'employee';
  teamId?: number | null;
  teamName?: string;
  employeeId?: number | null;
  employeeName?: string;
  fromEmployeeId?: number | null;
  fromEmployeeName?: string;
  isOpen: boolean;
  onClose: () => void;
};

type Employee = {
  id: number;
  fullName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  status?: string;
};

export default function ReplaceMemberDialog({
  type,
  teamId,
  teamName,
  employeeId,
  employeeName,
  fromEmployeeId,
  fromEmployeeName,
  isOpen,
  onClose,
}: ReplaceMemberDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReplacementId, setSelectedReplacementId] = useState<number | null>(null);
  const [transferLicenses, setTransferLicenses] = useState(false);
  const [transferTeamMemberships, setTransferTeamMemberships] = useState(false);

  const currentEmployeeId = type === 'team' ? fromEmployeeId : employeeId;
  const currentEmployeeName = type === 'team' ? fromEmployeeName : employeeName;

  // Fetch all employees for replacement selection
  const { data: allEmployees = [], isLoading: isLoadingEmployees } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: isOpen,
  });

  // Get available employees (excluding the current employee)
  const availableEmployees = allEmployees.filter(emp => 
    emp.id !== currentEmployeeId &&
    (emp.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
     emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
     emp.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     emp.department?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedEmployee = allEmployees.find(emp => emp.id === selectedReplacementId);

  // Replace team member mutation
  const replaceTeamMemberMutation = useMutation({
    mutationFn: async () => {
      if (!teamId || !fromEmployeeId || !selectedReplacementId) {
        throw new Error('Missing required data for team member replacement');
      }
      
      return apiRequest(`/api/teams/${teamId}/members/replace`, {
        method: 'POST',
        body: {
          fromEmployeeId,
          toEmployeeId: selectedReplacementId,
          transferLicenses,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams', teamId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
      
      toast({
        title: "Team member replaced",
        description: `Successfully replaced ${currentEmployeeName} with ${selectedEmployee?.fullName}${transferLicenses ? ' (licenses transferred)' : ''}`,
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to replace team member. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Replace employee mutation
  const replaceEmployeeMutation = useMutation({
    mutationFn: async () => {
      if (!employeeId || !selectedReplacementId) {
        throw new Error('Missing required data for employee replacement');
      }
      
      return apiRequest(`/api/employees/${employeeId}/replace`, {
        method: 'POST',
        body: {
          toEmployeeId: selectedReplacementId,
          transferLicenses,
          transferTeamMemberships,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      
      toast({
        title: "Employee replaced",
        description: `Successfully replaced ${currentEmployeeName} with ${selectedEmployee?.fullName}${transferLicenses ? ' (licenses transferred)' : ''}${transferTeamMemberships ? ' (team memberships transferred)' : ''}`,
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to replace employee. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleReplace = () => {
    if (type === 'team') {
      replaceTeamMemberMutation.mutate();
    } else {
      replaceEmployeeMutation.mutate();
    }
  };

  const isLoading = isLoadingEmployees;
  const isPending = replaceTeamMemberMutation.isPending || replaceEmployeeMutation.isPending;
  const canReplace = selectedReplacementId !== null;

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5" />
            Replace {type === 'team' ? 'Team Member' : 'Employee'}
          </DialogTitle>
          <DialogDescription>
            {type === 'team' 
              ? `Replace ${currentEmployeeName} in team "${teamName}" with another employee.`
              : `Replace ${currentEmployeeName} with another employee. You can transfer their licenses and team memberships.`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Current and replacement employee preview */}
        <div className="flex items-center gap-4 p-4 bg-muted rounded-lg flex-shrink-0">
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="h-8 w-8">
              <AvatarImage src="" />
              <AvatarFallback className="text-xs">
                {currentEmployeeName?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-medium">{currentEmployeeName}</div>
              <div className="text-xs text-muted-foreground">Current {type === 'team' ? 'member' : 'employee'}</div>
            </div>
          </div>
          
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          
          <div className="flex items-center gap-2 flex-1">
            {selectedEmployee ? (
              <>
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" />
                  <AvatarFallback className="text-xs">
                    {selectedEmployee.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium">{selectedEmployee.fullName}</div>
                  <div className="text-xs text-muted-foreground">Replacement</div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-8 w-8" />
                <div className="text-sm">Select replacement</div>
              </div>
            )}
          </div>
        </div>

        {/* Transfer options */}
        <div className="space-y-3 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="transfer-licenses"
              checked={transferLicenses}
              onCheckedChange={(checked) => setTransferLicenses(checked as boolean)}
              data-testid="checkbox-transfer-licenses"
            />
            <label
              htmlFor="transfer-licenses"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Transfer current licenses to replacement
            </label>
          </div>
          
          {type === 'employee' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="transfer-teams"
                checked={transferTeamMemberships}
                onCheckedChange={(checked) => setTransferTeamMemberships(checked as boolean)}
                data-testid="checkbox-transfer-teams"
              />
              <label
                htmlFor="transfer-teams"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Transfer team memberships to replacement
              </label>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4 flex-shrink-0">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search for replacement employee..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="input-search-replacement"
          />
        </div>

        {/* Available employees */}
        <ScrollArea className="flex-1 max-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : availableEmployees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? "No employees match your search" : "No available employees for replacement"}
            </div>
          ) : (
            <div className="space-y-2">
              {availableEmployees.map((employee) => {
                const isSelected = selectedReplacementId === employee.id;
                
                return (
                  <div
                    key={employee.id}
                    className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-colors ${
                      isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-muted"
                    }`}
                    onClick={() => setSelectedReplacementId(isSelected ? null : employee.id)}
                    data-testid={`employee-option-${employee.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src="" />
                        <AvatarFallback className="text-xs">
                          {employee.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{employee.fullName}</div>
                        <div className="text-xs text-muted-foreground">
                          {employee.email}
                          {employee.jobTitle && ` • ${employee.jobTitle}`}
                          {employee.department && ` • ${employee.department}`}
                        </div>
                        {employee.status && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {employee.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-1 font-medium">
                        Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-shrink-0">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isPending}
            data-testid="button-cancel-replace"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReplace} 
            disabled={!canReplace || isPending}
            data-testid="button-confirm-replace"
          >
            {isPending ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Replacing...
              </>
            ) : (
              'Replace Member'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}